import React, { useCallback } from 'react';
import { FormControl as MuiFormControl } from '@mui/material';
import { FormLabel as MuiFormLabel } from '@mui/material';
import { FormGroup as MuiFormGroup } from '@mui/material';
import { FormHelperText as MuiFormHelperText } from '@mui/material';
import { cx } from '@emotion/css';
import { useReactiveVar } from '@apollo/client';
import { useIntl } from 'react-intl';

import { Checkbox } from '../checkbox';
import { ICheckboxGroupProps } from './checkbox-group.models';
import { useStyles } from './checkbox-group.styles';
import { TFormExtendedCommonProps } from '../form';
import { NotificationsType } from '../../../graphql/profile/models/get-profile.models';
import { setMms, setIsPhoneCallConsent } from '../../../graphql/configuration/configuration.cache';

export const CheckboxGroup = ({
  title,
  options,
  value,
  onChange,
  helperText,
  error,
  ...restProps
}: TFormExtendedCommonProps<ICheckboxGroupProps>): JSX.Element => {
  const classes = useStyles();
  const intl = useIntl();
  const isMms = useReactiveVar(setMms);
  const isPhoneCallConsent = useReactiveVar(setIsPhoneCallConsent);

  const filteredOptions = options
    .filter((x: any) => isMms || x.value !== NotificationsType.mms)
    .filter((x: any) => isPhoneCallConsent || x.value !== NotificationsType.phoneCall);

  const handleChange = useCallback(
    (isChecked: boolean, optionValue: string) => {
      const newValue = {
        ...value,
      };

      if (isChecked) {
        delete newValue[optionValue];
      } else {
        newValue[optionValue] = true;
      }

      onChange(newValue);
    },
    [value, onChange],
  );

  return (
    <MuiFormControl
      fullWidth
      error={error}
      classes={{
        root: classes.formControlRoot,
      }}
    >
      {title && (
        <MuiFormLabel
          classes={{
            root: classes.formLabelRoot,
            focused: classes.formLabelFocused,
            error: classes.formLabelError,
          }}
        >
          {title}
        </MuiFormLabel>
      )}
      <MuiFormGroup {...restProps}>
        {filteredOptions.map(({ label, value: optionValue, labelStyles }) => {
          const isChecked = Boolean(value[optionValue]);

          return (
            <React.Fragment key={optionValue}>
              <Checkbox
                isInGroup
                label={label}
                checked={isChecked}
                onChange={() => handleChange(isChecked, optionValue)}
                labelStyles={cx(labelStyles, classes.label)}
              />
              {isChecked && optionValue === NotificationsType.mms && (
                <span className={classes.mmsText}>
                  {intl.formatMessage(
                    { id: 'profile.checkboxesText.mms' },
                    {
                      linebreak: <br />,
                      sup: (chunks: React.ReactNode) => <sup>{chunks}</sup>,
                    },
                  )}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </MuiFormGroup>
      {helperText && (
        <MuiFormHelperText
          classes={{
            root: classes.formHelperTextRoot,
            error: classes.formHelperTextError,
          }}
        >
          {helperText}
        </MuiFormHelperText>
      )}
    </MuiFormControl>
  );
};
